import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core'
import { TargetActionBlockInfo, TargetServiceBlockInfo, ServiceMapActionBlock, ServiceMapViewBlock } from '../service-map-view.interface'
import { ServiceMapViewService } from '../service-map-view.service'
import { ActuateRelationRuleForUi } from 'src/api/service-map-api/service-map-api.interface'

type SideAreaKey = 'connectedMore' | 'unconnected'
type SideAreaViewModel = {
  key: SideAreaKey
  label: string
  items: ServiceMapViewBlock[]
  open: boolean
  showCircle: boolean
}

declare const APP
@Component({
  selector: 'app-service-map-main',
  templateUrl: './service-map-main.component.html',
  styleUrls: ['./service-map-main.component.scss']
})
export class ServiceMapMainComponent implements OnInit, AfterViewInit, OnChanges {
  private highlightedPanel: HTMLElement = null
  private highlightTimerId: number = null

  @Input() serviceMap: ServiceMapViewBlock[] = []
  @Input() loading = false
  @Output() clickService = new EventEmitter<TargetServiceBlockInfo>()
  @Output() clickIcon = new EventEmitter<TargetServiceBlockInfo>()
  @Output() clickActionBlock = new EventEmitter<TargetActionBlockInfo>()
  serviceDivisions: string[] = []
  backServiceMap: ServiceMapViewBlock[] = []
  backConnectedMoreMap: ServiceMapViewBlock[] = []
  backUnconectedMap: ServiceMapViewBlock[] = []
  dispServiceMap: ServiceMapViewBlock[] = []
  dispConnectedMoreMap: ServiceMapViewBlock[] = []
  dispUnconectedMap: ServiceMapViewBlock[] = []
  sideAreas: SideAreaViewModel[] = []
  connectedChildByActuateCd: { [actuateCd: string]: ServiceMapViewBlock } = {}
  connectedOtherServiceByActuateCd: { [actuateCd: string]: ServiceMapViewBlock } = {}
  deepLevelChildrenByParentActuateCd: { [actuateCd: string]: ServiceMapViewBlock[] } = {}
  selectDivisionName = ''
  showDivisionPanel = false
  openConectedMoreArea = true
  openPackArea = true

  constructor(
    private serviceMapViewService: ServiceMapViewService
  ) { }

  async ngOnInit(): Promise<void> {
    const allMap = this.serviceMap
    const rules = this.serviceMapViewService.ActuateRelationRules || []

    // ランチャーが複数ある場合は連番を付ける
    const added: any[] = []
    allMap.forEach(block => {
      if (rules.find(x => x.toProductCd === block.productCd)?.showLauncherArea
        && allMap.filter(x => x.productCd === block.productCd).length > 1) {
        let addedProduct = added.find(x => x.productCd === block.productCd)
        if (!addedProduct) {
          added.push({
            productCd: block.productCd,
            count: 0
          })
          addedProduct = added.find(x => x.productCd === block.productCd)
        }
        addedProduct.count += 1
        block.launcherCount = addedProduct.count
      }
    })

    this.markDuplicateChildrenAsCircle(allMap)

    const connectedMap = this.serviceMapViewService.filterConnected(allMap)
    this.backUnconectedMap = this.serviceMapViewService
      .filterUnconnected(allMap)
      .filter(x => this.isUnconnectedTargetByRule(x, rules))
    this.backServiceMap = connectedMap.filter(x => {
      if (x.isLauncher || this.isKeepSingleLineProduct(x)) { return false }
      const hasParent = !!x.parentActuateIds?.length
      const hasChildren = !!x.children?.length
      // 親配下にぶら下がる leaf は丸表示のみで十分なのでトップ四角から除外
      return !(hasParent && !hasChildren)
    })
    this.backConnectedMoreMap = connectedMap.filter(x => x.isLauncher)
    this.rebuildConnectedBlockMaps(allMap)

    // 絞り込みプルダウン情報をセット
    this.serviceDivisions = []
    allMap.forEach((service) => {
      this.serviceDivisions.push(this.serviceMapViewService.divisionName(service))
    })
    this.changeDivision('')
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.ngOnInit()
  }

  changeDivision(division: string): void {
    if (!division) {
      this.dispServiceMap = this.backServiceMap
      this.dispConnectedMoreMap = this.backConnectedMoreMap
      this.dispUnconectedMap = this.backUnconectedMap
      this.selectDivisionName = '全て'
    } else if (division === 'unconnected') {
      this.dispServiceMap = []
      this.dispConnectedMoreMap = []
      this.dispUnconectedMap = this.backUnconectedMap
      this.selectDivisionName = '未接続'
    } else if (division === 'connectedMore') {
      this.dispServiceMap = []
      this.dispConnectedMoreMap = this.backConnectedMoreMap
      this.dispUnconectedMap = []
      this.selectDivisionName = 'ランチャー系'
    } else {
      this.dispServiceMap = this.backServiceMap.filter(
        (x) => this.serviceMapViewService.divisionName(x) === division
      )
      this.dispUnconectedMap = []
      this.selectDivisionName = division
    }
    this.rebuildSideAreas()
  }

  toggleSideArea(area: SideAreaKey): void {
    if (area === 'connectedMore') {
      this.openConectedMoreArea = !this.openConectedMoreArea
    }
    else {
      this.openPackArea = !this.openPackArea
    }
    this.rebuildSideAreas()
  }

  // 全ブロックが開いているか
  get arrowAllOpen(): boolean {
    if (!this.openPackArea || !this.openConectedMoreArea) { return false }
    let open = true
    this.dispServiceMap.forEach(block1 => {
      if (block1.childHidden) {
        open = false
      }
    })
    return open
  }

  // 全て展開する or 全て格納する ボタン押下時
  clickAllArrowButton(): void {
    if (this.arrowAllOpen) {
      // 全て格納
      this.backServiceMap.forEach(block1 => {
        block1.childHidden = true
      })
      this.openConectedMoreArea = false
      this.openPackArea = false
    }
    else {
      // 全て展開
      this.backServiceMap.forEach(block1 => {
        block1.childHidden = false
      })
      this.openConectedMoreArea = true
      this.openPackArea = true
    }
    this.rebuildSideAreas()
  }

  private rebuildSideAreas(): void {
    this.sideAreas = [
      {
        key: 'connectedMore',
        label: 'ランチャー系',
        items: this.dispConnectedMoreMap,
        open: this.openConectedMoreArea,
        showCircle: false
      },
      {
        key: 'unconnected',
        label: '未接続',
        items: this.dispUnconectedMap,
        open: this.openPackArea,
        showCircle: true
      }
    ]
  }

  private rebuildConnectedBlockMaps(allMap: ServiceMapViewBlock[]): void {
    this.connectedChildByActuateCd = {}
    this.connectedOtherServiceByActuateCd = {}
    this.deepLevelChildrenByParentActuateCd = {}

    allMap.forEach(block => {
      ;(block.parentActuateIds || []).forEach(parentActuateId => {
        if (!parentActuateId || this.connectedChildByActuateCd[parentActuateId]) { return }
        this.connectedChildByActuateCd[parentActuateId] = block
      })
    })

    const walk = (blocks: ServiceMapViewBlock[]): void => {
      blocks?.forEach(block => {
        const children = block.children || []
        const deepLevelChildren = children.filter(child => this.isKeepSingleLineProduct(child))
        const otherService = children.find(child => !this.isKeepSingleLineProduct(child) && child.serviceCd !== block.serviceCd)
        if (block.actuateId) {
          this.deepLevelChildrenByParentActuateCd[block.actuateId] = deepLevelChildren
          this.connectedOtherServiceByActuateCd[block.actuateId] = otherService
        }
        walk(block.children || [])
      })
    }
    walk(allMap)
  }

  private markDuplicateChildrenAsCircle(allMap: ServiceMapViewBlock[]): void {
    const appearanceByActuateId: { [actuateId: string]: number } = {}
    // 再帰的に階層数をカウントしながら走査
    const walk = (blocks: ServiceMapViewBlock[], floor: number): void => {
      blocks?.forEach(block => {
        block.forceCircleMode = false
        if (block.actuateId && block.parentActuateIds?.length) {
          const count = appearanceByActuateId[block.actuateId] || 0
          // 丸表示は3階層目または5階層目のみ許可
          if ((floor === 3 || floor === 5) && count > 0) {
            block.forceCircleMode = true
          }
          appearanceByActuateId[block.actuateId] = count + 1
        }
        walk(block.children || [], floor + 1)
      })
    }
    walk(allMap, 1)
  }

  private isKeepSingleLineProduct(block: ServiceMapViewBlock): boolean {
    if (!block) { return false }
    const rules = this.serviceMapViewService.ActuateRelationRules || []
    return rules.some(rule => rule.toProductCd === block.productCd && rule.keepSingleLine)
  }

  private isUnconnectedTargetByRule(
    block: ServiceMapViewBlock,
    rules: ActuateRelationRuleForUi[]
  ): boolean {
    if (!block) { return false }
    const rule = rules.find(x => x.toProductCd === block.productCd)
    if (!rule) { return false }
    return rule.showUnconnectedArea
  }

  clickServiceEvent(targetBlock: ServiceMapViewBlock, parentBlock: ServiceMapViewBlock, floorNumber: number, fromCircleMode = false): void {
    if (fromCircleMode) {
      this.scrollToSquareBlock(targetBlock)
    }
    const data: TargetServiceBlockInfo = {
      targetBlock, parentBlock, floorNumber
    }
    this.clickService.emit(data)
  }

  private scrollToSquareBlock(targetBlock: ServiceMapViewBlock): void {
    const actuateId = targetBlock?.actuateId
    if (!actuateId) { return }
    const selector = `[data-square-actuate-id="${actuateId}"]`
    const element = document.querySelector(selector) as HTMLElement
    if (!element) { return }
    element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    this.highlightSquareBlock(element)
  }

  private highlightSquareBlock(blockElement: HTMLElement): void {
    const panel = blockElement.querySelector('.c-panel') as HTMLElement
    if (!panel) { return }

    if (this.highlightedPanel) {
      this.clearHighlightStyle(this.highlightedPanel)
    }
    if (this.highlightTimerId) {
      window.clearTimeout(this.highlightTimerId)
      this.highlightTimerId = null
    }

    panel.style.transition = 'box-shadow 0.2s ease, background-color 0.2s ease'
    panel.style.boxShadow = '0 0 0 3px #f59e0b inset, 0 0 0 2px rgba(245, 158, 11, 0.35)'
    panel.style.backgroundColor = '#fff8e6'
    this.highlightedPanel = panel

    this.highlightTimerId = window.setTimeout(() => {
      if (!this.highlightedPanel) { return }
      this.clearHighlightStyle(this.highlightedPanel)
      this.highlightedPanel = null
      this.highlightTimerId = null
    }, 1800)
  }

  private clearHighlightStyle(panel: HTMLElement): void {
    panel.style.boxShadow = ''
    panel.style.backgroundColor = ''
    panel.style.transition = ''
  }

  clickIconEvent(targetBlock: ServiceMapViewBlock, parentBlock: ServiceMapViewBlock, floorNumber: number): void {
    const data: TargetServiceBlockInfo = {
      targetBlock, parentBlock, floorNumber
    }
    this.clickIcon.emit(data)
  }

  clickActionBlockEvent(targetBlock: ServiceMapActionBlock, parentBlock: ServiceMapViewBlock, floorNumber: number): void {
    const data: TargetActionBlockInfo = {
      targetBlock, parentBlock, floorNumber
    }
    this.clickActionBlock.emit(data)
  }

  ngAfterViewInit(): void {
    APP.controller.reset()
  }


}
